import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useQuery } from '@apollo/client/react';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { db } from '@/lib/local-api';
import { useCurrentChat } from '~/chat/hooks/useCurrentChat';

/**
 * Conversation actions for the GENERAL chat screen. Selection itself lives in
 * the route (`/chat/:niceId`, resolved by {@link useCurrentChat}); this hook
 * only mutates and navigates.
 *
 * A freshly created chat has a null `niceId` until the backend backfills it, so
 * both actions navigate to the bare `/chat` landing — which holds on the
 * newest chat and redirects to `/chat/:niceId` once one syncs in (see
 * `ChatIndexScreen`).
 */
export const useChatActions = () => {
  const powerSync = usePowerSync();
  const router = useRouter();
  const { t } = useTranslation('chat');
  const { data } = useQuery(CurrentUserDocument);
  const { chatId: currentChatId } = useCurrentChat();
  const userId = data?.currentUser?.id ?? '';
  const defaultName = t('list.defaultName');

  /** Start a fresh conversation (non-destructive) and open it via the landing. */
  const createNewChat = useCallback(async () => {
    if (!userId) return;
    await db.Chat.mutations.create(powerSync, userId, defaultName);
    router.push('/chat');
  }, [powerSync, userId, defaultName, router]);

  /**
   * Soft-delete a conversation. If it was the open one, fall back to the
   * landing so the session advances to the next most-recent chat (creating one
   * if none) instead of pointing at a deleted conversation.
   */
  const deleteChat = useCallback(
    async (chatId: string) => {
      await db.Chat.mutations.softDelete(powerSync, chatId);
      if (chatId === currentChatId) {
        router.replace('/chat');
      }
    },
    [powerSync, currentChatId, router],
  );

  return { createNewChat, deleteChat };
};
